import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hls from 'hls.js';
import Skeleton from '@mui/material/Skeleton';

const DetailBook = () => {
  const { bookId, bookName } = useParams();
  const [bookDetail, setBookDetail] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const videoRef = useRef(null);

  const proxyUrl = 'http://alabada.my.id/pro';

  const fetchAndModifyPlaylist = async (videoUrl) => {
    try {
      const proxyPlaylistUrl = `${proxyUrl}?url=${encodeURIComponent(videoUrl)}`;
      const response = await fetch(proxyPlaylistUrl);
      let playlist = await response.text();

      const baseUrl = videoUrl.substring(0, videoUrl.lastIndexOf('/') + 1);
      playlist = playlist.replace(/(.*\.ts)/g, (segment) =>
        `${proxyUrl}?url=${encodeURIComponent(baseUrl + segment)}`
      );

      const blob = new Blob([playlist], { type: 'application/vnd.apple.mpegurl' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching or modifying playlist:', error);
    }
  };

  // Load video data dan sesuaikan dengan sesi terakhir
  useEffect(() => {
    const loadLastSession = () => {
      const sessions = JSON.parse(localStorage.getItem('shortbox')) || [];
      const lastSession = sessions.find((session) => session.bookId === bookId);
      if (lastSession) {
        setCurrentVideoIndex(lastSession.currentVideoIndex);
      }
    };

    loadLastSession();
  }, [bookId]);

  useEffect(() => {
    const fetchBookVideos = async () => {
      try {
        const url = `https://shortbox.tv/api/collection/videos/${bookId}?locale=id&pageNum=1&pageSize=1000`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.ok) {
          const { total, data: videos } = data.data;
          setBookDetail({
            total,
            videos: videos.map((video) => ({
              videoUrl: video.path,
              subtitle: video.srts.find((srt) => srt.code === 'id')?.srtPath,
              position: video.position || 0,
              height: video.height || 720,
              width: video.width || 1280,
            })),
          });
        }
      } catch (error) {
        console.error('Error fetching book videos:', error);
      }
    };

    fetchBookVideos();
  }, [bookId]);

  useEffect(() => {
    const loadVideo = async () => {
      if (bookDetail && videoRef.current) {
        const video = videoRef.current;
        const currentVideo = bookDetail.videos[currentVideoIndex];
  
        if (Hls.isSupported() && currentVideo) {
          const hls = new Hls();
          const modifiedUrl = await fetchAndModifyPlaylist(currentVideo.videoUrl);
          if (modifiedUrl) {
            hls.loadSource(modifiedUrl);
            hls.attachMedia(video);
          }
  
          const { height, width } = currentVideo;
          const videoRatio = width / height;
          const maxHeight = window.innerHeight * 0.8;
          const maxWidth = window.innerWidth * 0.9;
          const calculatedWidth = Math.min(maxWidth, maxHeight * videoRatio);
          const calculatedHeight = calculatedWidth / videoRatio;
  
          video.style.width = `${calculatedWidth}px`;
          video.style.height = `${calculatedHeight}px`;
  
          // Hapus subtitle jika sudah ada
          const existingTrack = video.querySelector('track');
          if (existingTrack) {
            existingTrack.remove();
          }
  
          // Tambahkan subtitle jika ada
          if (currentVideo.subtitle) {
            const proxySubtitleUrl = `${proxyUrl}?url=${encodeURIComponent(currentVideo.subtitle)}`;
            const response = await fetch(proxySubtitleUrl);
            const text = await response.text();
  
            if (text) {
              const blob = new Blob([text], { type: 'text/vtt' });
              const subtitleUrl = URL.createObjectURL(blob);
  
              const track = document.createElement('track');
              track.kind = 'subtitles';
              track.label = 'Bahasa Indonesia';
              track.srclang = 'id';
              track.src = subtitleUrl;
              track.default = true;
  
              video.appendChild(track);
            }
          }
  
          video.addEventListener('ended', () => {
            if (currentVideoIndex < bookDetail.videos.length - 1) {
              setCurrentVideoIndex(currentVideoIndex + 1);
            }
          });
  
          return () => {
            hls.destroy();
          };
        }
      }
    };
  
    loadVideo();
  }, [bookDetail, currentVideoIndex]);

  // Simpan sesi terbaru
  useEffect(() => {
    if (bookDetail) {
      const lastSession = {
        bookId,
        currentVideoIndex,
        bookName,
        timestamp: new Date().toISOString(),
      };

      const sessions = JSON.parse(localStorage.getItem('shortbox')) || [];
      const updatedSessions = sessions.filter((session) => session.bookId !== bookId);
      updatedSessions.push(lastSession);

      localStorage.setItem('shortbox', JSON.stringify(updatedSessions));
    }
  }, [bookDetail, currentVideoIndex, bookName, bookId]);

  const handleVideoChange = (index) => {
    setCurrentVideoIndex(index);
  };

  if (!bookDetail) {
    return (
      <Box sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          {/* Skeleton untuk Video */}
          <Grid item xs={12} md={5}>
            <Box sx={{ marginBottom: 2, display: 'flex', justifyContent: 'center' }}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={0} // Rasio tinggi mengikuti desain video
                sx={{
                  paddingTop: '96.25%', // Rasio 16:9
                  borderRadius: 2,
                }}
              />
            </Box>
          </Grid>
  
          {/* Skeleton untuk Tombol */}
          <Grid item xs={12} md={7}>
            <Skeleton
              variant="text"
              sx={{
                marginBottom: 2,
                width: '40%',
                height: 24,
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                gap: 2,
                marginBottom: 2,
              }}
            >
              {Array.from({ length: 50 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  width="100%"
                  height={40}
                  sx={{
                    borderRadius: 1,
                  }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  const currentVideo = bookDetail.videos[currentVideoIndex];

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Box sx={{ marginBottom: 2, display: 'flex', justifyContent: 'center' }}>
            {currentVideo && (
              <video
                ref={videoRef}
                controls
                autoPlay
                style={{
                  display: 'block',
                  margin: '0 auto',
                }}
              >
                Your browser does not support the video tag.
              </video>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            Total Episodes: {bookDetail.total}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
              gap: 2,
              marginBottom: 2,
            }}
          >
            {bookDetail.videos.map((video, index) => (
              <Button
                key={index}
                variant={currentVideoIndex === index ? 'contained' : 'outlined'}
                onClick={() => handleVideoChange(index)}
                sx={{
                  padding: '6px 12px',
                  height: 'auto',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {index + 1}
              </Button>
            ))}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DetailBook;
